import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Circle, Github } from 'lucide-react';
import { LoadingSpinner } from '../components/common';
import { Navbar } from '../components/Navbar';
import Footer from '../components/Footer';
import { useAppContext } from '../contexts/useAppContext';
import { useSocket } from '../contexts/socketTypes';
import { ContainerService } from '../services/containerService';
import { useJWTAuth } from '../contexts/useJWTAuth';
import { logger } from '../utils/logger';

interface SetupStep {
  id: string;
  title: string;
  description: string;
  duration: number; // in seconds
  status: 'pending' | 'running' | 'completed' | 'error';
}

export const VirtualEnvironmentSetupPage: React.FC = () => {
  const { isAuthenticated, isInitialized } = useJWTAuth();
  const navigate = useNavigate();
  const { selectedRepository, setAnalysisData, isDemoMode, flowType } = useAppContext();

  // Redirect unauthenticated non-demo users to sign in
  useEffect(() => {
    if (isInitialized && !isAuthenticated && !isDemoMode && flowType !== 'demo') {
      sessionStorage.setItem('auth_return_url', window.location.pathname);
      navigate('/signin', { replace: true });
    }
  }, [isInitialized, isAuthenticated, isDemoMode, flowType, navigate]);
  const { socket } = useSocket();
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const [setupError, setSetupError] = useState<string | null>(null);
  // Dynamic steps based on flow type
  const getStepsForFlow = () => {
    if (flowType === 'demo') {
      return [
        {
          id: 'demo-setup',
          title: 'Loading Demo Project',
          description: 'Setting up sample project for demonstration...',
          duration: 2,
          status: 'pending' as const,
        },
        {
          id: 'demo-environment',
          title: 'Preparing Demo Environment',
          description: 'Creating virtual environment with demo data...',
          duration: 3,
          status: 'pending' as const,
        },
        {
          id: 'demo-analysis',
          title: 'Running Demo Analysis',
          description: 'Analyzing demo project with AI tools...',
          duration: 4,
          status: 'pending' as const,
        },
      ];
    } else if (flowType === 'upload') {
      return [
        {
          id: 'extract',
          title: 'Extracting ZIP File',
          description: 'Extracting and validating uploaded project...',
          duration: 3,
          status: 'pending' as const,
        },
        {
          id: 'environment',
          title: 'Setting Up Environment',
          description: 'Creating isolated virtual environment...',
          duration: 4,
          status: 'pending' as const,
        },
        {
          id: 'dependencies',
          title: 'Installing Dependencies',
          description: 'Installing required packages and analysis tools...',
          duration: 5,
          status: 'pending' as const,
        },
        {
          id: 'analysis',
          title: 'Preparing Analysis',
          description: 'Initializing AI analysis engines and scanners...',
          duration: 3,
          status: 'pending' as const,
        },
      ];
    } else {
      // Default repository flow
      return [
        {
          id: 'clone',
          title: 'Cloning Repository',
          description: 'Downloading your repository code securely...',
          duration: 3,
          status: 'pending' as const,
        },
        {
          id: 'environment',
          title: 'Setting Up Environment',
          description: 'Creating isolated virtual environment...',
          duration: 4,
          status: 'pending' as const,
        },
        {
          id: 'dependencies',
          title: 'Installing Dependencies',
          description: 'Installing required packages and analysis tools...',
          duration: 5,
          status: 'pending' as const,
        },
        {
          id: 'analysis',
          title: 'Preparing Analysis',
          description: 'Initializing AI analysis engines and scanners...',
          duration: 3,
          status: 'pending' as const,
        },
      ];
    }
  };

  const [steps, setSteps] = useState<SetupStep[]>(getStepsForFlow());

  useEffect(() => {
    // For demo mode, we don't need authentication or repository selection
    if (isDemoMode || flowType === 'demo') {
      // Start setup process after a brief delay
      const timer = setTimeout(() => {
        startSetup();
      }, 1000);
      return () => clearTimeout(timer);
    }

    // For regular flows, check if we have a repository
    if (!selectedRepository) {
      if (flowType === 'upload') {
        navigate('/upload');
      } else {
        navigate('/repositories');
      }
      return;
    }

    // Start setup process after a brief delay
    const timer = setTimeout(() => {
      startSetup();
    }, 1000);

    return () => clearTimeout(timer);
    // startSetup is defined inline below; running once per dep change is intentional
  }, [selectedRepository, navigate, isDemoMode, flowType]); // eslint-disable-line react-hooks/exhaustive-deps

  const startSetup = async () => {
    try {
      setSetupError(null);

      for (let i = 0; i < steps.length; i++) {
        setCurrentStepIndex(i);

        // Update step to running
        setSteps((prev) =>
          prev.map((step, index) => (index === i ? { ...step, status: 'running' } : step)),
        );

        // Simulate step duration with more realistic timing
        const stepDuration = steps[i].duration * 1000;
        await new Promise((resolve) => setTimeout(resolve, stepDuration));

        // Update step to completed
        setSteps((prev) =>
          prev.map((step, index) => (index === i ? { ...step, status: 'completed' } : step)),
        );
      }

      // Setup complete - prepare data
      const repositoryData = selectedRepository || {
        id: 'demo-project',
        name: 'demo-project',
        description: 'Sample project for demonstration',
        updatedAt: new Date().toISOString(),
        language: 'TypeScript',
        private: false,
        stars: 0,
        forks: 0,
        url: 'https://github.com/demo/demo-project',
        fullName: 'demo/demo-project',
        size: 1024,
        lastUpdated: new Date(),
        source: 'git' as const,
      };

      // Handle demo mode vs production mode differently
      if (isDemoMode || flowType === 'demo') {
        // Generate a UUID-style job ID for demo mode
        const hex = () => Math.random().toString(16).slice(2, 10);
        const mockJobId = `${hex()}-${hex().slice(0, 4)}-${hex().slice(0, 4)}-${hex()}`;

        // Store job ID in session for later use
        sessionStorage.setItem('currentContainerId', mockJobId);

        // Prepare mock analysis data
        const analysisData = {
          repository: repositoryData,
          containerId: mockJobId,
          analysisId: mockJobId,
          completedAt: new Date().toISOString(),
          results: {
            codeQuality: 92,
            security: 88,
            performance: 85,
            maintainability: 90,
          },
        };

        setAnalysisData(analysisData);

        // Navigate to demo analysis page
        setTimeout(() => {
          navigate(`/analysis/${mockJobId}`);
        }, 2000);

        return; // Exit early for demo mode
      }

      // Production mode - create actual container
      if (!socket?.id) {
        setSetupError('Socket connection not established. Please refresh and try again.');
        return;
      }

      try {
        const containerResponse = await ContainerService.createContainer(
          repositoryData.url || '/tmp/autardev-demo',
          socket.id,
        );

        const newContainerId = containerResponse.data?.containerId;
        if (!newContainerId) {
          throw new Error('No container ID returned from server');
        }

        // Store container ID in session for later use
        sessionStorage.setItem('currentContainerId', newContainerId);

        // Prepare analysis data
        const analysisData = {
          repository: repositoryData,
          containerId: newContainerId,
          analysisId: `analysis_${Date.now()}`,
          completedAt: new Date().toISOString(),
          results: {
            codeQuality: 85,
            security: 92,
            performance: 78,
            maintainability: 88,
          },
        };

        setAnalysisData(analysisData);

        // Navigate to analysis results page
        setTimeout(() => {
          navigate(`/jobs/${newContainerId}/results`);
        }, 2000);
      } catch (containerError: unknown) {
        logger.error('Container creation error', { error: containerError });
        setSetupError(
          `Failed to create container: ${containerError instanceof Error ? containerError.message : 'Unknown error'}`,
        );
        // Update the last step to error
        setSteps((prev) =>
          prev.map((step, index) =>
            index === steps.length - 1 ? { ...step, status: 'error' } : step,
          ),
        );
      }
    } catch (error: unknown) {
      logger.error('Setup error', { error });
      setSetupError(error instanceof Error ? error.message : 'An error occurred during setup');
    }
  };

  const overallProgress =
    currentStepIndex === -1 ? 0 : ((currentStepIndex + 1) / steps.length) * 100;
  const isComplete = steps.every((step) => step.status === 'completed');

  // Show loading only if we don't have repository and it's not demo mode
  if (!selectedRepository && !isDemoMode) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mx-auto mb-4" />
          <p style={{ color: 'var(--color-text-secondary)' }}>Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col transition-colors duration-200">
      <Navbar
        pageTitle="Setting Up Analysis"
        showDivider={true}
        leftContent={
          isDemoMode ? (
            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
              Demo
            </span>
          ) : undefined
        }
      />

      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Github className="w-6 h-6" style={{ color: 'var(--color-text-secondary)' }} />
              <h1 className="text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                {flowType === 'demo' ? 'Setting Up Demo' : 'Setting Up Analysis'}
              </h1>
            </div>
            <p style={{ color: 'var(--color-text-secondary)' }}>
              {flowType === 'demo'
                ? 'Preparing demo project for analysis...'
                : flowType === 'upload'
                  ? `Processing uploaded project: ${selectedRepository?.name}`
                  : `Preparing your repository: ${selectedRepository?.name}`}
            </p>
          </div>

          {/* Error Display */}
          {setupError && (
            <div className="mb-8 p-4 rounded-lg border border-red-200 bg-red-50">
              <div className="text-sm font-semibold text-red-800 mb-1">Setup Error</div>
              <div className="text-sm text-red-700">{setupError}</div>
              <button
                onClick={() => {
                  setSetupError(null);
                  setCurrentStepIndex(-1);
                  startSetup();
                }}
                className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
              >
                Retry
              </button>
            </div>
          )}

          {/* Overall Progress */}
          <div
            className="mb-8 p-6 rounded-lg"
            style={{ backgroundColor: 'var(--color-bg-secondary)' }}
          >
            <div
              className="flex justify-between text-sm mb-2"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              <span>Overall Progress</span>
              <span>{Math.round(overallProgress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="bg-linear-to-r from-teal-500 to-teal-600 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          </div>

          {/* Setup Steps */}
          <div className="space-y-4">
            {steps.map((step) => (
              <div
                key={step.id}
                className={`p-6 rounded-lg border transition-all duration-300 ${
                  step.status === 'completed'
                    ? 'border-green-200'
                    : step.status === 'running'
                      ? 'border-teal-300'
                      : 'border-gray-200'
                }`}
                style={{
                  backgroundColor:
                    step.status === 'completed'
                      ? 'rgba(34, 197, 94, 0.05)'
                      : step.status === 'running'
                        ? 'rgba(20, 184, 166, 0.05)'
                        : 'var(--color-bg-secondary)',
                }}
              >
                <div className="flex items-center">
                  <div className="shrink-0 mr-4">
                    {step.status === 'completed' ? (
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    ) : step.status === 'running' ? (
                      <LoadingSpinner size="sm" className="text-teal-500" />
                    ) : (
                      <Circle className="w-6 h-6" style={{ color: 'var(--color-text-tertiary)' }} />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3
                      className="font-semibold mb-1"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      {step.title}
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Completion Message */}
          {isComplete && (
            <div
              className="mt-8 text-center p-6 rounded-lg"
              style={{ backgroundColor: 'rgba(34, 197, 94, 0.05)' }}
            >
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h2
                className="text-xl font-semibold mb-2"
                style={{ color: 'var(--color-text-primary)' }}
              >
                Setup Complete!
              </h2>
              <p style={{ color: 'var(--color-text-secondary)' }}>
                Your repository has been successfully prepared for analysis. Redirecting to
                dashboard...
              </p>
              <div className="mt-4">
                <LoadingSpinner size="sm" className="mx-auto" />
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};
